import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';

import Wizard from '../../../../cabinet/wizplain';
import Step7 from './Step7';
import { convertReqToForm } from '../../../../cabinet/wizplain/helpers/convReqToForm';
import { getEmptyData } from '../../../../cabinet/wizplain/data/emptyform';
import { validateStep7 } from './validateStep7';
import { createFullAddressObject, formatAddress } from '../../../../cabinet/wizplain/helpers/address';
import apiClient from '../../../../../services/apiClient';
import { validate  } from './Validate';


// ---------------------------------------------------------------------------
// buildAcceptBody(formData, acceptData)
//
// Збирає body для POST completely_accept_request.
// Структура відповідає тому, що збирав старий AcceptChildren через blocks[].getData():
//
//   Блок 0  children   → flat: firstName, middleName, lastName, sex, birthDate
//   Блок 1  birthcert  → flat: birthCertificateNumber, birthCertificateIssueDate,
//                               birthCertificateCountry, address
//   Блок 2  parent(М)  → body.mother: { firstName, middleName, lastName, sitizen, code }
//   Блок 3  contacts(М)→ body.mother_contacts: { phone, email, address }
//   Блок 4  parent(Б)  → body.father: { firstName, middleName, lastName, sitizen, code }
//   Блок 5  contacts(Б)→ body.father_contacts: { phone, email, address }
//   Блок 6  benefit    → flat: benefitId, documentType, documentIssueDate, documentTo
//   Блок 7  health     → flat: illnesses, medicalProtocolNumber, medicalProtocolDate,
//                               invalidity, orphan, spec_educ
//   Блок 8  group      → flat: groupNumber, periodStartYear, placeType,
//                               number, smallNumber  (number/smallNumber — з acceptData)
//
// У новій системі в formData немає окремих mother/father — є один applicant
// (відповідальна особа). Батько/мати як окремі записи в старій схемі —
// перетворюємо responsiblePerson → mother або father залежно від relationDegree.
// ---------------------------------------------------------------------------

function buildAcceptBody(formData, acceptData, inviteData) {

  const { child, applicant, preferences, benefits, dictionary } = formData;
  const dict = dictionary?.addresses || {};

  // ── Дитина ──────────────────────────────────────────────────────────────
  const childFields = {
    firstName:   child.firstName  || '',
    middleName:  child.secondName || '',   // secondName = по батькові
    lastName:    child.lastName   || '',
    sex:         child.gender === 'girl' ? 'жін' : 'чол',
    birthDate:   child.birthDate  || '',
  };

  // ── Свідоцтво + адреса дитини ────────────────────────────────────────────
  const certFields = {
    birthCertificateNumber:    child.birthCertificate?.seriesNumber || '',
    birthCertificateIssueDate: child.birthCertificate?.issueDate    || '',
    birthCertificateCountry:   child.birthCertificate?.country      || '',
    address: child.isAddressKyiv === 'yes'
      ? formatAddress(child.addressYur, dict)
      : (child.address || ''),
  };

  // ── Відповідальна особа → mother або father ───────────────────────────────
  // У новій системі зберігається один applicant; relationDegree не зберігається
  // окремо, тому заповнюємо mother як основну контактну особу, father — порожнє.
  const parentObj = {
    firstName:  applicant.firstName  || '',
    middleName: applicant.middleName || '',
    lastName:   applicant.lastName   || '',
    sitizen:    applicant.citizenship === 'ua' ? 'Україна'
                  : applicant.citizenship === 'other' ? 'Іноземна держава'
                  : (applicant.citizenship || ''),
    code:       applicant.rnokpp || '',
  };

  const contactsObj = {
    phone:   applicant.phone || '',
    email:   applicant.email || '',
    address: formatAddress(applicant.address, dict) || applicant.addressStr || '',
  };

  // ── Пільги ───────────────────────────────────────────────────────────────
  const benefitFields = {
    benefitId:           benefits.type            || '',
    documentType:        benefits.documentType    || '',
    documentIssueDate:   benefits.issueDate       || '',
    documentTo:          benefits.issuedTo        || '',
  };

  // ── Здоров'я / спеціалізація ──────────────────────────────────────────────
  const healthFields = {
    illnesses:             preferences.diseaseType                   || null,
    medicalProtocolNumber: preferences.diseaseDocument?.number       || null,
    medicalProtocolDate:   preferences.diseaseDocument?.issueDate    || null,
    invalidity:            false,   // немає в новій формі — залишаємо default
    orphan:                false,
    spec_educ:             !!preferences.inclusiveGroup,
  };

  // ── Група (дані з invite + табельні з acceptData) ─────────────────────────
  const groupFields = {
    groupNumber:      inviteData.groupNumber     || '',
    periodStartYear:  inviteData.periodStartYear || '',
    placeType:        inviteData.placeType       || '',
    number:           acceptData.number          || '',
    smallNumber:      acceptData.smallNumber     || '',
  };

  // ── Збираємо фінальний body (flat + вкладені mother/father) ──────────────
  return {
    ...childFields,
    ...certFields,
    mother:          parentObj,
    mother_contacts: contactsObj,
    father:          { firstName: '', middleName: '', lastName: '', sitizen: '', code: '' },
    father_contacts: { phone: '', email: '', address: '' },
    ...benefitFields,
    ...healthFields,
    ...groupFields,
  };
}


const randomNumber = (digits) => 
  Math.floor(Math.random() * (10 ** digits - 10 ** (digits - 1)) + 10 ** (digits - 1)) + "";


// ---------------------------------------------------------------------------
// DeclineModal — мінімальний модаль для причини відмови
// ---------------------------------------------------------------------------

const DECLINE_REASONS = [
  { value: 'childfails',  label: 'В заявці зазначені документи іншої дитини' },
  { value: 'benefitfails', label: 'В заявці вказана недійсна пільга на вступ' },
  { value: 'parentfails', label: 'Особа, що принесла документи, не є батьками або опікуном' },
];

const DeclineModal = ({ show, onSubmit, onClose }) => {

  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = () => {
    if (!reason) { setError('Оберіть причину відмови'); return; }
    onSubmit({ declineReasonTypeId: reason, declineReason: description });
  };

  useEffect(() => { if (!show) { setReason(''); setDescription(''); setError(''); } }, [show]);

  return (
    <Modal show={show} onHide={onClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Відмовити у прийнятті дитини</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="form-group mb-3">
          <label className="form-label fw-semibold">Причина відмови <span className="text-danger">*</span></label>
          <select
            className={`form-select ${error ? 'is-invalid' : ''}`}
            value={reason}
            onChange={e => { setReason(e.target.value); setError(''); }}
          >
            <option value="">— оберіть причину —</option>
            {DECLINE_REASONS.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
          {error && <div className="invalid-feedback">{error}</div>}
        </div>
        <div className="form-group">
          <label className="form-label">Опис обставин відмови</label>
          <textarea
            className="form-control"
            rows={3}
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Детальний опис (необов'язково)"
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Скасувати</Button>
        <Button variant="danger" onClick={handleSubmit}>Надіслати повідомлення про відмову</Button>
      </Modal.Footer>
    </Modal>
  );
};


// ---------------------------------------------------------------------------
// AcceptChildrenNew
// ---------------------------------------------------------------------------

/**
 * Props:
 *   show          {boolean}
 *   idreq         {string}   — _id заявки
 *   invite        {object}   — { kindergartenId, groupNumber, groupId, placeType, periodStartYear }
 *   groups_info   {array}    — масив груп закладу (для пошуку groupId якщо немає в invite)
 *   user_info     {object}   — { token }
 *   onClose       {function}
 */
const AcceptChildrenNew = ({ show, idreq, invite = {}, groups_info = [], user_info = {}, onClose, getGrInfo, kgId }) => {

  // --- wizard state (передається у Wizard через setPersData/setPersStep) ---
  const [formData, setFormData]   = useState(getEmptyData());
  const [persStep, setPersStep]   = useState(6);

  // --- Step7 state ---
  const [acceptData, setAcceptData] = useState({ number: '', smallNumber: '' });
  const [step7Errors, setStep7Errors] = useState({});
  const [showStep7, setShowStep7]   = useState(true);

  // --- UI state ---
  const [showDecline, setShowDecline] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadData, setLoadData] = useState(false);
  const [inviteData, setInviteData] = useState({});

  // Завантаження даних заявки при відкритті
  useEffect(() => {
    if (!idreq || !show) return;

    setShowStep7(true);
    setAcceptData({ number: randomNumber(7), smallNumber: randomNumber(3)});
    setStep7Errors({});
    setPersStep(6);

    apiClient.get(`requests/${idreq}`)
      .then(res => {
        const data = res.data;
        if (!data || (Array.isArray(data) && data.length === 0)) {
          alert('Помилка отримання даних заявки');
          return;
        }
        const record = Array.isArray(data) ? data[0] : data;

        const invite = record.invite || {};
        
        if(!invite) return;

        const addInfoAboutGroup = getGrInfo(invite.groupNumber);

               
        if(addInfoAboutGroup._id) {
            invite["groupId"] = addInfoAboutGroup._id;
            invite["periodStartYear"] = addInfoAboutGroup.periodStartYear;
            invite["nameGroup"] = addInfoAboutGroup.name
        }        

        setFormData(convertReqToForm(record));
        setInviteData(invite);
        setLoadData(true);
      })
      .catch((err) => alert('Помилка отримання даних заявки' + err));
  }, [idreq, show]);

  // Визначаємо groupId (може бути в invite або знаходимо через groups_info)
  const resolveGroupId = () => {
    if (invite.groupId) return invite.groupId;
    const gr = groups_info.find(gr => gr.number == invite.groupNumber);
    return gr ? gr._id : null;
  };

  // ---------------------------------------------------------------------------
  // Перехід від Wizard (кроки 1-6) до Step7
  // Wizard викликає onFinish коли пройдено всі свої кроки.
  // ---------------------------------------------------------------------------
  const handleWizardFinish = () => {
    setShowStep7(true);
  };

  // ---------------------------------------------------------------------------
  // Зміна полів Step7
  // ---------------------------------------------------------------------------
  const handleAcceptChange = (field, value) => {
    setAcceptData(prev => ({ ...prev, [field]: value }));
    setStep7Errors(prev => ({ ...prev, [field]: undefined }));
  };

  // ---------------------------------------------------------------------------
  // Фінальне прийняття
  // ---------------------------------------------------------------------------
  const handleAccept = async () => {
    const errors = validateStep7(acceptData);
    if (Object.keys(errors).length > 0) {
      setStep7Errors(errors);
      return;
    }

    const errorReq = validate(formData);

    if (Object.keys(errorReq).length > 0) {
      setStep7Errors(errorReq);
      return;
    }

    const groupId = inviteData.groupId;

    if (!groupId) {
      alert('Не вдалося визначити групу. Перевірте налаштування запрошення.');
      return;
    }

    setIsSubmitting(true);
    try {
      const link = `kindergartens/${kgId}/groups/${groupId}/places/completely_accept_request/${idreq}`;

      const body = buildAcceptBody(formData, acceptData, invite);

      

      const result = await apiClient.post(link, body);
      alert('Дитина зарахована до закладу. Оновіть сторінку та перевірте наявність запису в групі.');
      handleClose();
    } catch (err) {
      alert(`Виникла помилка при зарахуванні. ${err?.message || ''}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Відмова
  // ---------------------------------------------------------------------------
  const handleDecline = async ({ declineReasonTypeId, declineReason }) => {
    const groupId = resolveGroupId();
    if (!groupId) {
      alert('Не вдалося визначити групу.');
      return;
    }

    try {
      const link = `kindergartens/${kgId}/groups/${encodeURIComponent(invite.groupNumber)}/places/completely_decline_request/${idreq}`;
      const result = await apiClient.post(link, { declineReason, declineReasonTypeId });
      alert('Заявка відхилена та переведена в стан заблокованої. Повідомте управління освіти про цей випадок.');
      handleClose();
    } catch (err) {
      alert(`Заявка не відхилена. ${err?.message || ''}`);
    }
  };

  const handleClose = () => {
    setShowStep7(true);
    setFormData(getEmptyData());
    setAcceptData({ number: '', smallNumber: '' });
    setStep7Errors({});
    setPersStep(6);
    onClose();
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <>
      {/* Основний модаль */}
      <style>{`
  .modal-fullwidth .modal-dialog {
    max-width: 90vw;
    width: 90vw;
    max-height: calc(100vh - 3.5rem);
    margin: 1.75rem auto;
    display: flex;
    flex-direction: column;
  }
  .modal-fullwidth .modal-content {
    height: 90vh;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .modal-fullwidth .modal-header,
  .modal-fullwidth .modal-footer {
    flex-shrink: 0;
  }
  .modal-fullwidth .modal-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
  }
`}</style>
      <Modal 
        show={show && !showDecline && isLoadData} 
        onHide={handleClose} 
        size="xl" 
        backdrop="static"
        dialogClassName="modal-fullwidth"
        >
        <Modal.Header closeButton>
          <Modal.Title>Оформлення дитини до закладу</Modal.Title>
        </Modal.Header>

        <Modal.Body>

          {!showStep7 ? (
            /* ---- Wizard: перегляд даних заявки (кроки 1-6) ---- */
            <Wizard
              isEdit={true}
              isReadOnly={true}
              isAccept={true}          /* замінює текст кнопки "Оновити" → "Далі до оформлення" */
              data={formData}
              step={persStep}
              setPersData={setFormData}
              setPersStep={setPersStep}
              dictionary={{}}
              onFinish={handleWizardFinish}
            />
          ) : (
            /* ---- Step7: оформлення (група + табельні номери) ---- */
            <Step7
              formData={formData}
              inviteData={inviteData}
              acceptData={acceptData}
              errors={step7Errors}
              onAcceptChange={handleAcceptChange}
            />
          )}

        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-between">
          <div className="d-flex gap-2">
            {showStep7 && (
              <>
                <Button
                  variant="secondary"
                  onClick={() => setShowStep7(false)}
                  disabled={isSubmitting}
                >
                  ← Переглянути заявку
                </Button>
                <Button
                  variant="success"
                  onClick={handleAccept}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Зарахування...' : 'Прийняти до закладу'}
                </Button>
              </>
            )}
          </div>
          <div className="d-flex gap-2">
            <Button variant="danger" onClick={() => setShowDecline(true)} disabled={isSubmitting}>
              Відмовити у прийнятті
            </Button>
            <Button variant="outline-secondary" onClick={handleClose} disabled={isSubmitting}>
              Закрити
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Модаль відмови */}
      <DeclineModal
        show={showDecline}
        onSubmit={handleDecline}
        onClose={() => setShowDecline(false)}
      />
    </>
  );
};

export default AcceptChildrenNew;